import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import toast from "react-hot-toast";
import { Dropdown } from "react-bootstrap";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Loader from "../../commanComponents/Loader";
import { communication } from "../../constants/apis/communication";
import { formatDateToDDMMYYYY } from "../../constants/helpers/helperFunctions";
import "./report.css";

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ val }) => {
  const badgeColor = () => {
    if (!val) return { bg: "#718096", color: "#fff" };
    const v = val.toLowerCase();
    if (v === "good" || v === "ok") return { bg: "#ffc107", color: "#000" };
    if (v === "notok" || v === "not ok") return { bg: "#dc3545", color: "#fff" };
    if (v === "new") return { bg: "#e3f2fd", color: "#1976d2" };
    if (v === "approved") return { bg: "#e8f5e9", color: "#2e7d32" };
    if (v === "rejected" || v === "reassigned") return { bg: "#fff3e0", color: "#ef6c00" };
    if (v === "pending_supervisor") return { bg: "#cfd8dc", color: "#263238" };
    if (v === "pending_hod") return { bg: "#ede7f6", color: "#5e35b1" };
    return { bg: "#718096", color: "#fff" };
  };

  const { bg, color } = badgeColor();
  return (
    <span
      style={{
        backgroundColor: bg,
        color: color,
        padding: "4px 12px",
        borderRadius: "20px",
        fontSize: "11px",
        fontWeight: "600",
        textTransform: "capitalize",
        display: "inline-block",
      }}
    >
      {val?.split("_").join(" ")}
    </span>
  );
};

export default function EquipmentReportComponent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get("reportId");
  const mineId = searchParams.get("mineId");
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({});

  const processLogData = (rawLogData) => {
    if (!rawLogData) return {};
    return {
      equipmentNumber: rawLogData.equipmentId?.equipmentId || "",
      equipmentCategory: rawLogData.equipmentId?.equipmentCategory || "",
      mineName: rawLogData.mineName || "",
      bdHrs: rawLogData.repairerFeedback?.breakdownHrs || "",
      workDone: rawLogData.repairerFeedback?.maintenanceHrs || "",
      machineName: rawLogData.equipmentId?.equipmentDescription || "",
      activityDate: rawLogData.repairerFeedback?.date || rawLogData.createdAt || "",
      shift: rawLogData.repairerFeedback?.shift?.shift || "",
      breakdownHrs: rawLogData.repairerFeedback?.breakdownHrs || "",
      maintenanceWork: rawLogData.repairerFeedback?.maintenanceHrs || "",
      mechanicName: rawLogData.repairerFeedback?.mechanicName || "",
      remark: rawLogData.repairerFeedback?.repairReason || rawLogData.hodRemarks || "",
      supervisorId: rawLogData.supervisorId?.name || "",
      approvalStatus: rawLogData.status || "",
    };
  };

  const { data: logDetails, isFetching } = useQuery(["getMaintenanceById", reportId, mineId], () => communication?.getMaintenanceById({ id: reportId, mineId }), {
    enabled: !!reportId,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data?.data?.status === "JWT_INVALID" || data?.data?.status === "FAILED") {
        toast.error(`Failed: ${data?.data?.message}`);
      } else if (data?.data?.data) {
        setFormData(processLogData(data.data.data));
      }
    },
    onError: (error) => {
      toast.error(`Failed: ${error?.message}`);
    },
  });

  const logData = processLogData(logDetails?.data?.data);

  // Define columns mapped exactly to the required response fields
  const columns = [
    { header: "Sr. No", key: "srNo", editable: false },
    { header: "Equipment Number", key: "equipmentNumber", editable: true },
    { header: "Equipment Category", key: "equipmentCategory", editable: true },
    { header: "Mine Name", key: "mineName", editable: true },
    { header: "BD Hrs", key: "bdHrs", editable: true },
    { header: "Work Done", key: "workDone", editable: true },
    { header: "Machine Name", key: "machineName", editable: true },
    { header: "Activity Date", key: "activityDate", editable: true },
    { header: "Shift", key: "shift", editable: true },
    { header: "Breakdown Hrs", key: "breakdownHrs", editable: true },
    { header: "Maintenance Work", key: "maintenanceWork", editable: true },
    { header: "Mechanic Name", key: "mechanicName", editable: true },
    { header: "Remark", key: "remark", editable: true },
    { header: "Incharge Name", key: "supervisorId", editable: false },
    { header: "Approval Status", key: "approvalStatus", editable: false },
  ];

  const handleInputChange = (e, key) => {
    setFormData((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));
  };

  const handleSave = () => {
    toast.error("Save functionality is not implemented yet");
    // setEditMode(false);
  };

  const getCellValue = (col) => {
    const key = col.key;
    const dataSource = editMode ? formData : logData;

    if (editMode && col.editable) {
      return (
        <input
          type="text"
          value={dataSource[key] || ""}
          onChange={(e) => handleInputChange(e, key)}
          style={{
            padding: "4px 8px",
            border: "1px solid #d1d5db",
            borderRadius: "4px",
            fontSize: "12px",
            width: "120px",
            outline: "none",
          }}
        />
      );
    }

    if (key === "srNo") return "1";
    if (key === "activityDate" && dataSource[key]) return formatDateToDDMMYYYY(dataSource[key]);
    if (key === "approvalStatus" && dataSource[key]) return <StatusBadge val={dataSource[key]} />;
    if (key === "machineStatus" && dataSource[key]) return <StatusBadge val={dataSource[key]} />;

    return dataSource[key] || "--";
  };

  // ─── Export Logic ─────────────────────────────────────────────────────────
  const getDateTimeString = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(now.getDate())}${pad(now.getMonth() + 1)}${now.getFullYear()}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  };

  const getExportData = () => {
    const rowData = {};
    columns.forEach((col) => {
      const dataSource = editMode ? formData : logData;
      let val = dataSource[col.key];

      if (col.key === "srNo") val = "1";
      else if (col.key === "activityDate" && val) val = formatDateToDDMMYYYY(val);

      rowData[col.header] = val || "--";
    });
    return [rowData];
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(getExportData());
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Equipment Report");
    XLSX.writeFile(wb, `Equipment_Report_${getDateTimeString()}.xlsx`);
  };

  const exportToCSV = () => {
    const ws = XLSX.utils.json_to_sheet(getExportData());
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Equipment_Report_${getDateTimeString()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    try {
      const currentLog = editMode ? formData : logData;

      // 1. Initialize Landscape A4 Document
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth(); // 297mm
      const pageHeight = pdf.internal.pageSize.getHeight(); // 210mm
      const margin = 10;
      const usableWidth = pageWidth - margin * 2; // 277mm

      // ─── 2. HEADER SECTION ────────────────────────────────────────────────
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      const mineTitle = `MOIL LIMITED - ${(currentLog.mineName || "DONGRI BUZURG MINE").toUpperCase()}`;
      pdf.text(mineTitle, pageWidth / 2, 14, { align: "center" });

      pdf.setFontSize(11);

      // ─── 3. SUB-HEADER METADATA BLOCK ────────────────────────────────────
      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "bold");

      // Left side metadata
      pdf.text(`Equipment No: ${currentLog.equipmentNumber || "--"}`, margin, 28);
      pdf.text(`Machine Name: ${currentLog.machineName || "--"}`, margin, 33);
      pdf.text(`Machine Status: ${(currentLog.machineStatus || "--").toUpperCase()}`, margin, 38);

      // Right side metadata
      const dateStr = formatDateToDDMMYYYY(currentLog.activityDate || currentLog.createdAt || new Date());
      pdf.text(`DATE: ${dateStr}`, pageWidth - margin, 28, { align: "right" });
      pdf.text(`SHIFT: ${currentLog.shift || "--"}`, pageWidth - margin, 33, { align: "right" });
      pdf.text(`LEVEL: ${currentLog.equipmentCategory || "--"}`, pageWidth - margin, 38, { align: "right" });

      // ─── 4. DATA COMPILATION ─────────────────────────────────────────────
      const tableHeaders = columns.map((col) => col.header);
      const secondaryHeaders = columns.map((_, idx) => String(idx + 1));

      const dataRow = columns.map((col) => {
        const key = col.key;
        if (key === "srNo") return "1";
        if (key === "activityDate") return dateStr;

        return currentLog[key] !== undefined && currentLog[key] !== null && currentLog[key] !== "" ? String(currentLog[key]) : "--";
      });

      // ─── 5. AUTOTABLE MATRIX GENERATION ──────────────────────────────────
      const totalCols = columns.length;
      const srNoWidth = 8;
      const remainingWidth = usableWidth - srNoWidth;
      const defaultColWidth = remainingWidth / (totalCols - 1);

      const columnStyles = {};
      columns.forEach((col, idx) => {
        if (col.key === "srNo") {
          columnStyles[idx] = { cellWidth: srNoWidth };
        } else {
          columnStyles[idx] = { cellWidth: defaultColWidth };
        }
      });

      autoTable(pdf, {
        startY: 44,
        margin: { left: margin, right: margin },
        head: [
          [
            {
              content: "EQUIPMENT REPORT",
              colSpan: columns.length,
              styles: { halign: "center", fontStyle: "bold", fillColor: [243, 244, 246], textColor: [17, 24, 39], fontSize: 9, cellPadding: 2.5 },
            },
          ],
          tableHeaders,
          secondaryHeaders,
        ],
        body: [dataRow],
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 1.5,
          overflow: "hidden",
          halign: "center",
          valign: "middle",
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.15,
        },
        columnStyles: columnStyles,
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },
        didParseCell: function (data) {
          if (data.row.index === 1 && data.section === "head") {
            // Give enough room for vertical rotated text titles
            data.cell.styles.minCellHeight = 40;
          }
          if (data.row.index === 2 && data.section === "head") {
            data.cell.styles.fillColor = [243, 244, 246];
            data.cell.styles.fontSize = 6.5;
          }
        },
        willDrawCell: function (data) {
          if (data.row.index === 1 && data.section === "head") {
            pdf.saveGraphicsState();

            // Calculate exact visual center of the cell box area
            const cx = data.cell.x + data.cell.width / 2;
            const cy = data.cell.y + data.cell.height - 2;

            pdf.setFontSize(6.2);
            pdf.setFont("helvetica", "bold");

            const headerText = data.cell.raw ? String(data.cell.raw) : data.cell.text.join(" ") || "";

            // Safe vertical string generation strategy without overriding layout alignment keys
            pdf.text(headerText, cx, cy, {
              angle: 45,
            });

            pdf.restoreGraphicsState();
            data.cell.text = [];
            return false;
          }
        },
      });

      // ─── 6. SIGNATURE FOOTER TRACK ───────────────────────────────────────
      const finalTableY = pdf.lastAutoTable.finalY || 100;
      const signatureY = Math.min(finalTableY + 22, pageHeight - 20);

      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "bold");

      // Left Section: Verified Status Badge

      // Right Section: Supervisor / Manager
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8.5);
      pdf.setTextColor(0, 0, 0);
      const supervisorName = currentLog.supervisorId?.name || currentLog.supervisorId || "--";
      pdf.text(supervisorName, pageWidth - margin - 25, signatureY, { align: "center" });
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(75, 85, 99);
      pdf.text("Supervisor", pageWidth - margin - 25, signatureY + 4, { align: "center" });

      // Save PDF output
      pdf.save(`Equipment_Report_${getDateTimeString()}.pdf`);
      toast.success("PDF generated successfully");
    } catch (err) {
      console.error("PDF Export error details: ", err);
      toast.error("Failed to generate PDF");
    }
  };

  return (
    <div style={{ backgroundColor: "#f3f4f6", minHeight: "100vh", padding: "32px" }}>
      {isFetching && <Loader />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#111827", margin: 0 }}>Equipment Report</h2>

        <div style={{ display: "flex", gap: "12px" }}>
          {editMode ? (
            <>
              <button
                onClick={handleSave}
                style={{
                  backgroundColor: "#2e7d32",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  transition: "all 0.2s",
                }}
              >
                Save
              </button>
              <button
                onClick={() => {
                  setFormData(logData);
                  setEditMode(false);
                }}
                style={{
                  backgroundColor: "#dc3545",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  transition: "all 0.2s",
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {/* <button
                onClick={() => setEditMode(true)}
                style={{
                  backgroundColor: "#2f3299",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "6px",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: "pointer",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  transition: "all 0.2s",
                }}
              >
                Edit
              </button> */}

              <Dropdown>
                <Dropdown.Toggle
                  style={{
                    backgroundColor: "#10b981",
                    color: "#fff",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontWeight: "600",
                    fontSize: "14px",
                    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  }}
                  id="dropdown-export"
                >
                  Export
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ borderRadius: "8px", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", border: "1px solid #e5e7eb" }}>
                  <Dropdown.Item onClick={exportToExcel} style={{ fontSize: "14px", fontWeight: "500", color: "#374151", padding: "8px 16px" }}>
                    Export to Excel
                  </Dropdown.Item>
                  <Dropdown.Item onClick={exportToCSV} style={{ fontSize: "14px", fontWeight: "500", color: "#374151", padding: "8px 16px" }}>
                    Export to CSV
                  </Dropdown.Item>
                  <Dropdown.Item onClick={exportToPDF} style={{ fontSize: "14px", fontWeight: "500", color: "#374151", padding: "8px 16px" }}>
                    Export to PDF
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}

          <button
            onClick={() => navigate(-1)}
            style={{
              backgroundColor: "#ffffff",
              color: "#374151",
              border: "1px solid #d1d5db",
              padding: "8px 16px",
              borderRadius: "6px",
              fontWeight: "600",
              fontSize: "14px",
              cursor: "pointer",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              transition: "all 0.2s",
            }}
          >
            Go Back
          </button>
        </div>
      </div>

      <div className="spreadsheet-container" style={{ borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)" }}>
        {logData && Object.keys(logData).length > 0 ? (
          <table className="spreadsheet-table">
            <thead>
              {/* Main Headers */}
              <tr>
                {columns.map((col, index) => (
                  <th key={`header-${index}`}>{col.header}</th>
                ))}
              </tr>
              {/* Secondary Header (Numbers) matching the screenshot */}
              <tr>
                {columns.map((_, index) => (
                  <th key={`num-${index}`}>{index + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {columns.map((col, index) => (
                  <td key={`cell-${index}`} className="spreadsheet-td-val">
                    {getCellValue(col)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        ) : (
          !isFetching && <div style={{ textAlign: "center", padding: "40px", color: "#6b7280" }}>No log details found.</div>
        )}
      </div>

      {logData?.logbook?.length > 0 && (
        <div style={{ marginTop: "32px", backgroundColor: "#fff", padding: "24px", borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "700", color: "#111827", marginBottom: "16px", borderBottom: "1px solid #e5e7eb", paddingBottom: "8px" }}>Gang Activities (Logbook)</h3>
          {logData.logbook.map((gang, idx) => (
            <div key={idx} style={{ marginBottom: "24px" }}>
              <div style={{ display: "flex", gap: "24px", marginBottom: "12px" }}>
                <div style={{ fontSize: "14px", color: "#374151" }}>
                  <strong style={{ color: "#111827" }}>Gang No:</strong> {gang.gangNo || "N/A"}
                </div>
                <div style={{ fontSize: "14px", color: "#374151" }}>
                  <strong style={{ color: "#111827" }}>Gang Man:</strong> {gang.gangMan || "N/A"}
                </div>
              </div>
              {gang.logbookArray && gang.logbookArray.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table className="spreadsheet-table" style={{ width: "100%", minWidth: "auto" }}>
                    <thead>
                      <tr>
                        <th>Job Code</th>
                        <th>Job Description</th>
                        <th>Quantity</th>
                        <th>Unit</th>
                        <th>Rate</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gang.logbookArray.map((job, jIdx) => (
                        <tr key={jIdx}>
                          <td>{job.jobCode || "-"}</td>
                          <td>{job.jobDescription || "-"}</td>
                          <td>{job.quantity || "-"}</td>
                          <td>{job.unit || "-"}</td>
                          <td>{job.rate || "-"}</td>
                          <td>{job.amount || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ fontSize: "13px", color: "#6b7280" }}>No activities found for this gang.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
